import ActionBar from "../../components/ActionBar/ActionBar";
import Spinner from "../../components/Spinner/Spinner";
import CircleChart from "../../components/CircleChart/CircleChart";
import { colorOptions } from "../../utils/colors";
import { useState, useEffect, useCallback } from "react";
import useAuthStore from "../../store/authStore";
import { getOrganizations } from "../../api/organizations/organizations";
import { fetchProjects } from "../../api/projects/index";
import { getTeams } from "../../api/teams/teams";
import { getAllTasks } from "../../api/tasks/getAll";
import { getAllNotes } from "../../api/notes/getAll";
import { useSocketEvent } from "../../hooks/useRealtime";
import { useSocket } from "../../context/SocketContext";
import { useTranslation } from "react-i18next";
import "./Dashboard.scss";

// Named colors from colors.js for easy inline usage
const COLORS = {
  purple: colorOptions[15], // orchid
  green: colorOptions[7], // forest
  orange: colorOptions[2], // coral
  yellow: colorOptions[4], // lemon
  blue: colorOptions[12], // denim
  pink: colorOptions[16], // magenta
  teal: colorOptions[9], // turquoise
  rose: colorOptions[1], // raspberry
  gray: colorOptions[21], // slate
  indigo: colorOptions[13], // slate
};

// Helper function to format subscription display
const getSubscriptionLabel = (subscription, t) => {
  const labels = {
    free: t("dashboard.subscription.free"),
    trial: t("dashboard.subscription.trial"),
    paid: t("dashboard.subscription.paid"),
    active: t("dashboard.subscription.paid"), // Legacy mapping if "active" is still in DB
    past_due: t("dashboard.subscription.pastDue"),
    cancelled: t("dashboard.subscription.cancelled"),
    canceled: t("dashboard.subscription.cancelled"),
  };
  return (
    labels[subscription] || subscription || t("dashboard.subscription.free")
  );
};

// Display names for the stored user roles
const USER_ROLE_KEYS = {
  user: "dashboard.userRoles.user",
  admin: "people.roles.admin",
};

// English keeps the "3rd of November 2026" style, other languages use their
// own date format
const formatTrialEndDate = (date, language) => {
  const isEnglish = language?.startsWith("en");
  const formatted = new Date(date).toLocaleDateString(
    isEnglish ? "en-GB" : language,
    {
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  );
  if (!isEnglish) return formatted;
  return formatted.replace(/(\d+)/, (day) => {
    const num = parseInt(day);
    const suffix =
      num === 1 || num === 21 || num === 31
        ? "st"
        : num === 2 || num === 22
          ? "nd"
          : num === 3 || num === 23
            ? "rd"
            : "th";
    return `${num}${suffix} of`;
  });
};

function Dashboard() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const { joinOrganization, leaveOrganization } = useSocket();
  const [organizations, setOrganizations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalNotes, setTotalNotes] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [orgs, projs] = await Promise.all([
        getOrganizations(token),
        fetchProjects(user?.id),
      ]);

      // Filter only active organizations and projects
      const activeOrgs = orgs.filter((org) => org.isActive !== false);
      const activeProjs = projs.filter((proj) => proj.isActive !== false);

      setOrganizations(activeOrgs);
      setProjects(activeProjs);

      // Fetch teams from all active organizations
      const allTeams = [];
      for (const org of activeOrgs) {
        try {
          const orgTeams = await getTeams(org._id, token);
          // Filter only active teams
          const activeTeams = orgTeams.filter(
            (team) => team.isActive !== false,
          );
          allTeams.push(...activeTeams);
        } catch (error) {
          console.error(`Error fetching teams for org ${org._id}:`, error);
        }
      }
      setTeams(allTeams);

      // Fetch tasks from all active projects
      let taskCount = 0;
      for (const proj of activeProjs) {
        try {
          const taskGroups = await getAllTasks(false, proj._id); // false = don't show completed
          // Count all tasks from all task groups
          taskGroups.forEach((group) => {
            if (group.tasks) {
              // Only count active tasks
              taskCount += group.tasks.filter(
                (task) => task.isActive !== false,
              ).length;
            }
          });
        } catch (error) {
          console.error(`Error fetching tasks for project ${proj._id}:`, error);
        }
      }
      setTotalTasks(taskCount);

      // Fetch notes from all active projects
      let noteCount = 0;
      for (const proj of activeProjs) {
        try {
          const noteGroups = await getAllNotes(false, proj._id); // false = don't show completed
          // Count all notes from all note groups
          noteGroups.forEach((group) => {
            if (group.notes) {
              // Only count active notes
              noteCount += group.notes.filter(
                (note) => note.isActive !== false,
              ).length;
            }
          });
        } catch (error) {
          console.error(`Error fetching notes for project ${proj._id}:`, error);
        }
      }
      setTotalNotes(noteCount);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  // Single useEffect - load all data on mount, then rely on WebSocket events
  useEffect(() => {
    if (token && user?.id) {
      fetchData();

      // Join organization rooms after initial data load
      const loadOrgsAndJoinRooms = async () => {
        const orgs = await getOrganizations(token);
        orgs.forEach((org) => {
          joinOrganization(org._id);
        });
      };

      loadOrgsAndJoinRooms();
    }

    // Cleanup: leave all rooms when component unmounts
    return () => {
      if (organizations.length > 0) {
        organizations.forEach((org) => {
          leaveOrganization(org._id);
        });
      }
    };
    // Only run on mount and unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time updates via WebSocket - refetch data when changes occur
  // Listen for organization events
  const handleOrganizationCreated = useCallback(async () => {
    // Refresh user data to get updated fields (memberSince, isActive, etc.)
    const authStore = useAuthStore.getState();
    await authStore.refreshUserData();
    // Then fetch dashboard data
    fetchData();
  }, [fetchData]);

  useSocketEvent("organization:created", handleOrganizationCreated);
  useSocketEvent("organization:updated", fetchData);
  useSocketEvent("organization:deleted", fetchData);

  // Listen for project events
  useSocketEvent("project:created", fetchData);
  useSocketEvent("project:updated", fetchData);
  useSocketEvent("project:deleted", fetchData);

  // Listen for team events
  useSocketEvent("team:created", fetchData);
  useSocketEvent("team:updated", fetchData);
  useSocketEvent("team:deleted", fetchData);

  // Listen for task events
  useSocketEvent("task:created", fetchData);
  useSocketEvent("task:updated", fetchData);
  useSocketEvent("task:deleted", fetchData);
  useSocketEvent("task:bulk-deleted", fetchData);

  // Listen for note events
  useSocketEvent("note:created", fetchData);
  useSocketEvent("note:updated", fetchData);
  useSocketEvent("note:deleted", fetchData);
  useSocketEvent("note:bulk-deleted", fetchData);

  // Billing calculation: $8 per member per organization owned
  // Calculate future billing (what will be charged after trial)
  const PRICE_PER_MEMBER_PER_ORG = 8;
  const isOnTrial = user?.subscription === "trial" && user?.trialEndsAt;

  const ownedOrganizations = organizations.filter(
    (org) => org.ownerId === user?.id,
  );
  // Only count active members for billing (not pending invites)
  const totalMembersInOwnedOrgs = ownedOrganizations.reduce((total, org) => {
    const activeMembers =
      org.members?.filter((m) => m.status === "active").length || 0;
    return total + activeMembers;
  }, 0);

  // Current billing: $0 during trial, otherwise calculate based on members
  const currentBilling = isOnTrial
    ? 0
    : totalMembersInOwnedOrgs * PRICE_PER_MEMBER_PER_ORG;

  // Future billing after trial ends
  const futureBilling = totalMembersInOwnedOrgs * PRICE_PER_MEMBER_PER_ORG;

  // Calculate organization role breakdown
  const ownerOrgs = organizations.filter(
    (org) => org.ownerId === user?.id,
  ).length;
  const adminOrgs = organizations.filter((org) => {
    if (org.ownerId === user?.id) return false; // Don't count owner as admin
    const member = org.members.find((m) => m.userId === user?.id);
    return member?.role === "admin";
  }).length;
  const memberOnlyOrgs = organizations.filter((org) => {
    if (org.ownerId === user?.id) return false; // Don't count owner
    const member = org.members.find((m) => m.userId === user?.id);
    return member?.role === "member";
  }).length;

  const totalOrgsTile = ownerOrgs + adminOrgs + memberOnlyOrgs;

  const orgsTileChart = [
    {
      percentage: (ownerOrgs / totalOrgsTile) * 100,
      color: COLORS.indigo,
      label: t("people.roles.owner"),
      value: ownerOrgs,
    },
    {
      percentage: (adminOrgs / totalOrgsTile) * 100,
      color: COLORS.green,
      label: t("people.roles.admin"),
      value: adminOrgs,
    },
    {
      percentage: (memberOnlyOrgs / totalOrgsTile) * 100,
      color: COLORS.blue,
      label: t("people.roles.member"),
      value: memberOnlyOrgs,
    },
  ];

  // Calculate project role breakdown
  const ownProjects = projects.filter(
    (proj) => proj.ownerId === user?.id,
  ).length;
  const adminProjects = projects.filter((proj) => {
    if (proj.ownerId === user?.id) return false; // Don't count owner as admin
    const member = proj.members.find((m) => m.userId === user?.id);
    return member?.role === "admin";
  }).length;
  const memberProjects = projects.filter((proj) => {
    if (proj.ownerId === user?.id) return false; // Don't count owner
    const member = proj.members.find((m) => m.userId === user?.id);
    return member?.role === "user";
  }).length;

  const totalProjectsTile = ownProjects + adminProjects + memberProjects;

  const projectsTileChart = [
    {
      percentage: (ownProjects / totalProjectsTile) * 100,
      color: COLORS.indigo,
      label: t("dashboard.chart.ownProjects"),
      value: ownProjects,
    },
    {
      percentage: (adminProjects / totalProjectsTile) * 100,
      color: COLORS.green,
      label: t("people.roles.admin"),
      value: adminProjects,
    },
    {
      percentage: (memberProjects / totalProjectsTile) * 100,
      color: COLORS.blue,
      label: t("people.roles.member"),
      value: memberProjects,
    },
  ];

  // Calculate teams breakdown
  // "My own Teams" = teams in organizations where user is the owner
  const ownedOrgIds = organizations
    .filter((org) => org.ownerId === user?.id)
    .map((org) => org._id);

  const ownTeams = teams.filter((team) =>
    ownedOrgIds.includes(team.organizationId),
  ).length;

  // Teams where user is a member (in organizations user doesn't own)
  const memberTeams = teams.filter((team) => {
    // Only count teams in orgs we don't own
    if (ownedOrgIds.includes(team.organizationId)) return false;
    // Check if user is a member
    return team.members?.some((m) => m.userId === user?.id);
  }).length;

  const totalTeamsTile = ownTeams + memberTeams;

  const teamsTileChart = [
    {
      percentage: (ownTeams / totalTeamsTile) * 100,
      color: COLORS.indigo,
      label: t("dashboard.chart.ownTeams"),
      value: ownTeams,
    },
    {
      percentage: (memberTeams / totalTeamsTile) * 100,
      color: COLORS.blue,
      label: t("people.roles.member"),
      value: memberTeams,
    },
  ];

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <div className="dashboard">
          <div className="dashboard-content">
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard">
        <div className="dashboard-content">
          <div className="dashboard-main-tile">
            {/* <div className="main-title">Profile</div> */}

            <div className="content user-profile">
              <div className="profile-avatar">
                <div
                  className="avatar-circle"
                  style={{ backgroundColor: user?.profile?.color || "#2382e4" }}
                >
                  {user?.firstName?.charAt(0) || "U"}
                  {user?.lastName?.charAt(0) || ""}
                </div>
              </div>

              <div className="profile-tables">
                <table className="profile-info-table">
                  <tbody>
                    <tr>
                      <td className="info-label">
                        {t("dashboard.profile.name")}
                      </td>
                      <td className="info-value">
                        {user?.firstName} {user?.lastName}
                      </td>
                    </tr>
                    <tr>
                      <td className="info-label">
                        {t("dashboard.profile.email")}
                      </td>
                      <td className="info-value">{user?.email}</td>
                    </tr>
                    <tr>
                      <td className="info-label">
                        {t("dashboard.profile.memberSince")}
                      </td>
                      <td className="info-value">
                        {user?.memberSince
                          ? new Date(user.memberSince).toLocaleDateString(
                              i18n.language,
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              },
                            )
                          : t("account.notAvailable")}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table className="profile-info-table">
                  <tbody>
                    <tr>
                      <td className="info-label">
                        {t("dashboard.profile.role")}
                      </td>
                      <td className="info-value">
                        <span className="role-badge">
                          {user?.role
                            ? USER_ROLE_KEYS[user.role]
                              ? t(USER_ROLE_KEYS[user.role])
                              : user.role
                            : t("dashboard.userRoles.user")}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="info-label">
                        {t("dashboard.profile.status")}
                      </td>
                      <td className="info-value">
                        <span
                          className={`status-badge ${user?.isActive ? "active" : "inactive"}`}
                        >
                          {user?.isActive
                            ? t("dashboard.profile.active")
                            : t("dashboard.profile.inactive")}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="info-label">
                        {t("dashboard.profile.subscription")}
                      </td>
                      <td className="info-value">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <span className="subscription-badge">
                            {getSubscriptionLabel(user?.subscription, t)}
                          </span>
                          {isOnTrial && (
                            <span style={{ fontSize: "11px", color: "#999" }}>
                              {t("dashboard.trialEndsAfter", {
                                date: formatTrialEndDate(
                                  user.trialEndsAt,
                                  i18n.language,
                                ),
                                amount: futureBilling,
                              })}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="dashboard-tiles">
            <div className="dashboard-tile">
              <div className="tile-title purple">
                <div className="tile-icon">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "27px", color: COLORS.indigo }}
                  >
                    domain
                  </span>
                </div>
                <div className="tile-title-text" style={{ color: "white" }}>
                  {t("dashboard.tiles.organizations")}
                </div>
              </div>
              <div
                className="content"
                style={{ marginLeft: "-70px", marginTop: "25px" }}
              >
                <CircleChart
                  segments={orgsTileChart}
                  size={150}
                  strokeWidth={10}
                  centerValue={totalOrgsTile}
                  centerLabel={t("dashboard.chart.total")}
                  showLegend={true}
                  legendPosition="right"
                  showValues={true}
                  animate={true}
                />
              </div>
            </div>
            <div className="dashboard-tile">
              <div className="tile-title">
                <div className="tile-icon">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "27px", color: COLORS.indigo }}
                  >
                    folder_copy
                  </span>
                </div>
                <div className="tile-title-text" style={{ color: "white" }}>
                  {t("organizations.projectsLabel")}
                </div>
              </div>
              <div
                className="content"
                style={{ marginLeft: "-70px", marginTop: "25px" }}
              >
                <CircleChart
                  segments={projectsTileChart}
                  size={150}
                  strokeWidth={10}
                  centerValue={totalProjectsTile}
                  centerLabel={t("dashboard.chart.total")}
                  showLegend={true}
                  legendPosition="right"
                  showValues={true}
                  animate={true}
                />
              </div>
            </div>
            <div className="dashboard-tile">
              <div className="tile-title purple">
                <div className="tile-icon">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "27px", color: COLORS.indigo }}
                  >
                    group
                  </span>
                </div>
                <div className="tile-title-text" style={{ color: "white" }}>
                  {t("people.teams")}
                </div>
              </div>
              <div
                className="content"
                style={{ marginLeft: "-70px", marginTop: "25px" }}
              >
                <CircleChart
                  segments={teamsTileChart}
                  size={150}
                  strokeWidth={10}
                  centerValue={totalTeamsTile}
                  centerLabel={t("dashboard.chart.total")}
                  showLegend={true}
                  legendPosition="right"
                  showValues={true}
                  animate={true}
                />
              </div>
            </div>

            <div className="dashboard-tile">
              <div className="tile-title orange">
                <div className="tile-icon">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "27px", color: COLORS.orange }}
                  >
                    checklist
                  </span>
                </div>
                <div className="tile-title-text" style={{ color: "white" }}>
                  {t("dashboard.tiles.tasksTotal")}
                </div>
              </div>
              <div className="content">
                <div className="number">{totalTasks}</div>
                {/* <div className="description">Total Tasks</div> */}
              </div>
            </div>
            {/* <div className="dashboard-tile-dummy"></div> */}
            <div className="dashboard-tile">
              <div className="tile-title orange">
                <div className="tile-icon">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "27px", color: COLORS.orange }}
                  >
                    note_stack
                  </span>
                </div>
                <div className="tile-title-text" style={{ color: "white" }}>
                  {t("dashboard.tiles.notesTotal")}
                </div>
              </div>
              <div className="content">
                <div className="number">{totalNotes}</div>
                {/* <div className="description">Total Organizations</div> */}
              </div>
            </div>
            <div className="dashboard-tile">
              <div className="tile-title green">
                <div className="tile-icon">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "27px", color: COLORS.green }}
                  >
                    payments
                  </span>
                </div>
                <div className="tile-title-text" style={{ color: "white" }}>
                  {t("dashboard.tiles.billing")}
                </div>
              </div>
              <div
                className="content"
                style={{ flexDirection: "column", gap: "0.5rem" }}
              >
                <div className="number" style={{ color: COLORS.green }}>
                  ${currentBilling}
                  <span
                    style={{
                      fontSize: "24px",
                      color: COLORS.green,
                      fontWeight: "normal",
                      marginLeft: "8px",
                    }}
                  >
                    {t("dashboard.perMonth")}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "white",
                    marginTop: "-20px",
                    textAlign: "center",
                    paddingLeft: "15px",
                    paddingRight: "15px",
                  }}
                >
                  {isOnTrial ? (
                    <>
                      <span style={{ fontSize: "11px", opacity: 0.9 }}>
                        {t("dashboard.trialEndsAfterTrial", {
                          date: formatTrialEndDate(
                            user.trialEndsAt,
                            i18n.language,
                          ),
                          amount: futureBilling,
                        })}
                      </span>
                    </>
                  ) : (
                    <>
                      {t("dashboard.billingBreakdown", {
                        price: PRICE_PER_MEMBER_PER_ORG,
                        members: totalMembersInOwnedOrgs,
                        count: ownedOrganizations.length,
                      })}
                    </>
                  )}
                </div>
                {/* <div className="description">Total Organizations</div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
